import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { APP } from 'src/app/config/app';
import { ConfirmationModalState, IClientInfoDraft, IClientInfoGroup, ISelectElement } from 'src/app/models';
import { ClientsService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ModalConfirmationComponent } from 'src/app/shared/modals/modal-confirmation/modal-confirmation.component';
import { TooltipModule } from 'src/app/shared/tooltip/tooltip.module';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { ClientInfoFormComponent } from '../client-info-form/client-info-form.component';

@Component({
  selector: 'app-client-info-configurations',
  templateUrl: './client-info-configurations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./client-info-configurations.component.scss'],
  imports: [ClientInfoFormComponent, ModalConfirmationComponent, TooltipModule],
})
export class ClientInfoConfigurationsComponent {
  readonly clientId = input.required<number>();
  readonly edit = model(false);

  readonly images = images;
  readonly TooltipPosition = TooltipPosition;

  readonly groups = resource<IClientInfoGroup[], number>({
    params: () => this.clientId(),
    loader: ({ params: clientId }) => this.clientsService.getClientInfoGroups(clientId),
  });
  readonly clientTowns = resource<ISelectElement[], number>({
    params: () => this.clientId(),
    loader: async ({ params: clientId }) => {
      const towns = await this.clientsService.searchClientTowns(clientId, {
        page: 1,
        size: APP.SUPERADMIN_FORM_OPTIONS_PAGE_SIZE,
      });
      return towns.value.map((town) => ({ id: town.id!, name: town.name ?? '' }));
    },
  });
  readonly defaultGroup = computed(() => this.groups.value()?.find((group) => group.defaultInfo) ?? null);
  readonly townGroups = computed(() => this.groups.value()?.filter((group) => !group.defaultInfo) ?? []);
  readonly townGroupItems = computed(() =>
    this.townGroups().map((group) => ({ group, townNames: group.towns.map((town) => town.name).join(', ') })),
  );
  readonly selectedGroupId = signal<number | null>(null);
  readonly creating = signal(false);
  readonly selectedTownGroup = computed(() =>
    this.creating() ? null : (this.townGroups().find((group) => group.id === this.selectedGroupId()) ?? null),
  );
  readonly editingDefault = computed(() => !this.creating() && !this.selectedTownGroup());
  readonly selectedInfo = computed(() => {
    if (this.creating()) return null;
    return (this.selectedTownGroup() ?? this.defaultGroup())?.info ?? null;
  });
  readonly selectedTownIds = computed(() => this.selectedTownGroup()?.towns.map((town) => town.id!) ?? []);
  readonly availableTowns = computed<ISelectElement[] | null>(() => {
    if (this.editingDefault()) return null;
    const ownTownIds = new Set(this.selectedTownIds());
    const assignedTownIds = new Set(
      this.townGroups()
        .flatMap((group) => group.towns.map((town) => town.id))
        .filter((townId) => !ownTownIds.has(townId!)),
    );
    return (this.clientTowns.value() ?? []).filter((town) => !assignedTownIds.has(town.id as number));
  });
  readonly formTitle = computed(() => {
    if (this.creating()) return this.literalService.get('clients.info.newConfiguration', true);
    const townGroup = this.selectedTownGroup();
    return townGroup
      ? this.townGroupItems().find((item) => item.group.id === townGroup.id)?.townNames
      : this.literalService.get('clients.info.defaultConfiguration', true);
  });
  readonly saving = signal(false);
  readonly deleteModal = new ConfirmationModalState();

  private readonly deleteModalComponent = viewChild.required(ModalConfirmationComponent);

  constructor(
    public literalService: LiteralService,
    private clientsService: ClientsService,
    private utilsService: UtilsService,
  ) {}

  selectDefault() {
    this.creating.set(false);
    this.selectedGroupId.set(null);
    this.edit.set(false);
  }

  selectGroup(group: IClientInfoGroup) {
    this.creating.set(false);
    this.selectedGroupId.set(group.id);
    this.edit.set(false);
  }

  startCreation() {
    this.selectedGroupId.set(null);
    this.creating.set(true);
    this.edit.set(true);
  }

  onCancelled() {
    this.creating.set(false);
  }

  async save(draft: IClientInfoDraft) {
    if (this.saving()) return;
    const { townIds, ...clientInfo } = draft;
    this.saving.set(true);
    try {
      if (this.editingDefault()) {
        await this.clientsService.saveClientInfo(this.clientId(), clientInfo);
      } else {
        const savedGroup = await this.clientsService.saveClientInfoGroup(
          this.clientId(),
          { info: clientInfo, townIds },
          this.selectedTownGroup()?.id,
        );
        this.creating.set(false);
        this.selectedGroupId.set(savedGroup.id);
      }
      this.groups.reload();
      this.edit.set(false);
      this.utilsService.showSuccessNotification('superadminZone.saved');
    } finally {
      this.saving.set(false);
    }
  }

  showDeleteConfigurationModal() {
    this.deleteModal.showModal(this.deleteModalComponent().modalContainer().nativeElement);
  }

  deleteConfigurationAction() {
    const group = this.selectedTownGroup();
    if (!group) return;
    this.deleteModal.run(async () => {
      await this.clientsService.deleteClientInfoGroup(this.clientId(), group.id);
      this.selectDefault();
      this.groups.reload();
      this.utilsService.showSuccessNotification('superadminZone.deleted');
    });
  }
}
